import { useSearchParams } from "next/navigation";

import getSearchResults from "@/api/requests/getSearchResults";
import { SearchPageTypes } from "@/custom-types/Search";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useDispatch, useSelector } from "@/redux/hooks";
import { getCoordinates } from "@/redux/selectors/location";
import {
  getIsNullResult,
  getSearchCategoryRelevanceOrder,
  getSearchData,
  getSearchLoading,
  getSearchMenuItemDispensaries,
  getTotalCounts,
} from "@/redux/selectors/search";
import {
  DEFAULT_SEARCH_SKIP,
  DEFAULT_SEARCH_TAKE,
  loadMore,
  MAX_DISTANCE,
  setLoading,
  setResults,
} from "@/redux/slices/search";

// * Adding a max distance to the menu items piece of global search.
// * https://leafly.atlassian.net/browse/SHH-438
const MAX_DISTANCE_FILTER = {
  "filter[menu_items_max_distance]": "300",
};

const useSearch = () => {
  const searchParams = useSearchParams();
  const q = searchParams?.get("q");
  const searchCategory = searchParams?.get("searchCategory");
  const dispatch = useDispatch();

  const domainCountryCode = useDomainCountryCode();

  const coordinates = useSelector(getCoordinates);
  const loading = useSelector(getSearchLoading);
  const results = useSelector(getSearchData);
  const menuItemDispensaries = useSelector(getSearchMenuItemDispensaries);
  const categoryRelevanceOrder = useSelector(getSearchCategoryRelevanceOrder);
  const nullResults = useSelector(getIsNullResult);
  const totalCounts = useSelector(getTotalCounts);

  const getResults = async (
    take = DEFAULT_SEARCH_TAKE,
    skip = DEFAULT_SEARCH_SKIP,
  ) => {
    dispatch(setLoading());

    const {
      article,
      brand,
      dispensary,
      menuItem,
      strain,
      menuItemDispensaries,
      categoryRelevanceOrder,
    } = await getSearchResults(
      { q, ...MAX_DISTANCE_FILTER },
      coordinates,
      MAX_DISTANCE,
      domainCountryCode,
      take,
      skip,
      "",
      true,
    );

    dispatch(
      setResults({
        article,
        brand,
        categoryRelevanceOrder,
        dispensary,
        menuItem,
        menuItemDispensaries,
        strain,
      }),
    );
  };

  const loadMoreResults = async (type: SearchPageTypes, take: number) => {
    const additionalItems = await getSearchResults(
      { "filter[type]": type, q, ...MAX_DISTANCE_FILTER },
      coordinates,
      MAX_DISTANCE,
      domainCountryCode,
      take,
      results[type].length,
      "",
      true,
    );

    const newResults = [...results[type], ...additionalItems[type]];

    dispatch(
      loadMore({
        totalCounts: {
          [type]: additionalItems.metadata.totalCount,
        },
        [type]: newResults,
      }),
    );

    return additionalItems.metadata.totalCount;
  };

  return {
    loading,
    ...results,
    categoryRelevanceOrder,
    getSearchResults: getResults,
    loadMoreSearchResults: loadMoreResults,
    menuItemDispensaries,
    nullResults,
    searchCategory: searchCategory ? String(searchCategory) : undefined,
    searchTerm: q ? String(q) : "",
    totalCounts,
  };
};

export default useSearch;
